import { useCallback, useMemo } from 'react';
import {
  getProject,
  useProject,
  useUpdateProject,
} from '@client/store/project/project.hook';
import {
  getProjectMeta,
  updateBuilding,
} from '@shared/helpers/project.helpers';
import { Project } from '@shared/models/project.model';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { getScopeTargetKey } from '@shared/helpers/scope-targets.helpers';
import { createLocalStorageStore } from '@client/helpers/local-storage.helpers';
import { useActivityMix } from '@features/design/hooks/activities.hooks';
import { ScopeTarget } from '@features/lifecycles/scope-presets.data';
import {
  getWeightedTargetValue,
  targetGradesScope,
} from './scope-target.helpers';
import { getScope, ResolvedScope, useScope } from './scope.store';

/**
 * Target a scope starts from, in kgCO2e per m² GFA, when it publishes no limit
 * value of its own and none has been set by hand.
 */
export const DEFAULT_SCOPE_TARGET = 200;

const {
  useStore: useScopeTargetLevelStore,
  set: setScopeTargetLevels,
  get: getScopeTargetLevels,
} = createLocalStorageStore<Record<string, string>>('scope_target_levels', {});

/**
 * Every hook below reads the current scope by default, and the one it is handed
 * instead when there is one — which is how the overlay previews what a scope
 * would report while its preset row is hovered.
 */
type ScopeOverride = ResolvedScope | undefined;

const useScopeOrOverride = (override: ScopeOverride): ResolvedScope => {
  const current = useScope();
  return override ?? current;
};

/**
 * The level of the scope the fallback is read from, by scope key. A scope that
 * grades several levels (Miljöbyggnad Silver and Guld, BREEAM's credit levels)
 * is one scope with one target — which level it aims at is a choice, kept per
 * scope so switching scope and back keeps it.
 */
const useSelectedScopeLevelId = (scope?: ScopeOverride): string | undefined => {
  const key = useScopeTargetKey(scope);
  return useScopeTargetLevelStore((levels) => levels?.[key]);
};

/**
 * The levels the active scope can be read against, in the order the preset lists
 * them. Only levels that grade what the scope reports: a limit value written for
 * narrower modules (Guld under the full-lifecycle scope) measures something else
 * than the result on screen, so the scope has no target of its own to fall back
 * on and lands on {@link DEFAULT_SCOPE_TARGET}.
 */
export const useScopeLevels = (
  scope?: ScopeOverride,
): readonly ScopeTarget[] => {
  const { preset, lifecycles } = useScopeOrOverride(scope);

  return useMemo(
    () =>
      (preset?.targets ?? []).filter((target) =>
        targetGradesScope(target, lifecycles),
      ),
    [preset, lifecycles],
  );
};

/**
 * The level the fallback target is taken from: the one picked for this scope,
 * or the scope's first level. Undefined when the scope grades no level.
 */
export const useSelectedScopeLevel = (
  scope?: ScopeOverride,
): ScopeTarget | undefined => {
  const levels = useScopeLevels(scope);
  const selectedId = useSelectedScopeLevelId(scope);
  return levels.find(({ id }) => id === selectedId) ?? levels[0];
};

/** Read the fallback target from this level of the current scope from now on. */
export const selectScopeLevel = (levelId: string): void =>
  setScopeTargetLevels({
    ...getScopeTargetLevels(),
    [getScopeTargetKey(getScope())]: levelId,
  });

/**
 * The limit value the selected level publishes, resolved for the project's
 * activity mix — Miljöbyggnad's Silver value under the embodied scope, and so
 * on. Undefined when the scope grades no level, or when a relative level has no
 * reference values to cut.
 */
const useScopeLimitValue = (scope?: ScopeOverride): number | undefined => {
  const { preset } = useScopeOrOverride(scope);
  const level = useSelectedScopeLevel(scope);
  const activityMix = useActivityMix();

  return useMemo(
    () =>
      level &&
      getWeightedTargetValue(level, activityMix, preset?.referenceValues),
    [level, preset, activityMix],
  );
};

/**
 * What the current scope is measured against while it has no target of its own:
 * the scope's published limit value, or {@link DEFAULT_SCOPE_TARGET} when it
 * publishes none.
 */
export const useFallbackTarget = (scope?: ScopeOverride): number => {
  const resolved = useScopeOrOverride(scope);
  const limit = useScopeLimitValue(resolved);
  return limit ?? resolved.preset?.target ?? DEFAULT_SCOPE_TARGET;
};

/** Every target the project holds, by scope key. */
const getScopeTargets = (project: Project): Record<string, number> =>
  getProjectMeta(project).scope_targets ?? {};

/** Key of the scope the results are shown in. */
export const useScopeTargetKey = (scope?: ScopeOverride): string => {
  const resolved = useScopeOrOverride(scope);
  return useMemo(() => getScopeTargetKey(resolved), [resolved]);
};

/**
 * The stored target for the current scope. Undefined when
 * {@link ProjectMetadata.scope_targets} has no entry for it.
 */
export const useProjectScopeTarget = (
  scope?: ScopeOverride,
): number | undefined => {
  const project = useProject();
  const key = useScopeTargetKey(scope);
  return getScopeTargets(project)[key];
};

/**
 * The target the current scope is measured against: the scope's own, or the
 * fallback when it has not been given one.
 */
export const useResolvedProjectTarget = (scope?: ScopeOverride): number => {
  const scopeTarget = useProjectScopeTarget(scope);
  const fallback = useFallbackTarget(scope);
  return scopeTarget ?? fallback;
};

/** How a scope's target reads, wherever it is shown. */
export type ScopeTargetLabel = {
  /**
   * The graded level the value comes from, when it comes from one — dropped for
   * a hand-written target, which belongs to no level. Carries no qualifier that
   * only restates the value ("1 credit", not "1 credit (10 %)").
   */
  levelName?: string;
  /** The target itself, formatted for display. */
  value: string;
};

/**
 * What the scope falls back to, spelled out — what the target field previews in
 * its placeholder, including while a target of its own is being cleared.
 */
export const useFallbackTargetLabel = (
  scope?: ScopeOverride,
): ScopeTargetLabel => {
  const fallback = useFallbackTarget(scope);
  const level = useSelectedScopeLevel(scope);

  return useMemo(
    () => ({
      levelName: level && (level.shortTargetName ?? level.targetName),
      value: getDisplayNumber(fallback),
    }),
    [fallback, level],
  );
};

/**
 * The target the scope is actually measured against, as the scope selector and
 * the overview's target line both spell it out, so the two cannot drift apart.
 * A hand-written target replaces the fallback and belongs to no level.
 */
export const useScopeTargetLabel = (
  scope?: ScopeOverride,
): ScopeTargetLabel => {
  const manualTarget = useProjectScopeTarget(scope);
  const fallbackLabel = useFallbackTargetLabel(scope);

  return useMemo(
    () =>
      manualTarget === undefined
        ? fallbackLabel
        : { value: getDisplayNumber(manualTarget) },
    [manualTarget, fallbackLabel],
  );
};

/**
 * The project with one scope's target written, or dropped when no target is
 * given. The whole metadata object is replaced rather than merged through
 * `updateProjectMetadata`: its deep merge can add and change keys but never
 * remove one, so a cleared target would survive as the old value.
 */
export const withScopeTarget = (
  project: Project,
  scopeKey: string,
  target?: number,
): Project => {
  const meta = getProjectMeta(project);
  const scopeTargets = { ...getScopeTargets(project) };
  if (target === undefined) delete scopeTargets[scopeKey];
  else scopeTargets[scopeKey] = target;

  return updateBuilding(project, {
    meta: { ...meta, scope_targets: scopeTargets },
  });
};

/**
 * Write the project's target for the current scope. Passing undefined drops it,
 * which hands the scope back to {@link useFallbackTarget}.
 */
export const useSetProjectScopeTarget = (): ((target?: number) => void) => {
  const updateProject = useUpdateProject();

  return useCallback(
    (target?: number) => {
      const project = getProject();
      const key = getScopeTargetKey(getScope());
      if (getScopeTargets(project)[key] === target) return;

      void updateProject({
        kind: 'snapshot',
        project: withScopeTarget(project, key, target),
      });
    },
    [updateProject],
  );
};
